/**
 * Plain-English readings of the S-57 codes the chart data carries, so nothing
 * user-facing shows a raw database abbreviation. Pure functions, no map or DOM
 * dependency, so they're easy to unit test.
 *
 * The light-character expansion gives the readable sentence alongside the
 * standard chart abbreviation (which we keep, because that's what's taught and
 * what's on paper charts).
 */

// S-57 light character (LITCHR): code -> [abbreviation, plain English].
const LITCHR: Record<number, [string, string]> = {
  1: ['F', 'Fixed'],
  2: ['Fl', 'Flashing'],
  3: ['LFl', 'Long flashing'],
  4: ['Q', 'Quick'],
  5: ['VQ', 'Very quick'],
  6: ['UQ', 'Ultra quick'],
  7: ['Iso', 'Isophase'],
  8: ['Oc', 'Occulting'],
  9: ['IQ', 'Interrupted quick'],
  10: ['IVQ', 'Interrupted very quick'],
  11: ['IUQ', 'Interrupted ultra quick'],
  12: ['Mo', 'Morse'],
  13: ['FFl', 'Fixed and flashing'],
  14: ['FlLFl', 'Flashing and long flashing'],
  15: ['OcFl', 'Occulting and flashing'],
  16: ['FLFl', 'Fixed and long flashing'],
  17: ['Al.Oc', 'Alternating occulting'],
  18: ['Al.LFl', 'Alternating long flashing'],
  19: ['Al.Fl', 'Alternating flashing'],
  20: ['Al.Gr', 'Alternating group'],
  25: ['Q+LFl', 'Quick plus long flash'],
  26: ['VQ+LFl', 'Very quick plus long flash'],
  27: ['Al', 'Alternating'],
  28: ['Mo', 'Morse'],
  29: ['F+Fl', 'Fixed plus flashing'],
};

// Colour letter -> plain name.
const COLOUR: Record<string, string> = {
  W: 'white',
  B: 'black',
  R: 'red',
  G: 'green',
  Bu: 'blue',
  Y: 'yellow',
  Gy: 'grey',
  Br: 'brown',
  Am: 'amber',
  Vi: 'violet',
  Or: 'orange',
  Mg: 'magenta',
  Pk: 'pink',
};

// Object class acronym -> plain name (the ones we actually surface).
const OBJCLASS: Record<string, string> = {
  LIGHTS: 'Light',
  LNDMRK: 'Landmark',
  BOYLAT: 'Lateral buoy',
  BOYCAR: 'Cardinal buoy',
  BOYSAW: 'Safe-water buoy',
  BOYSPP: 'Special buoy',
  BOYISD: 'Isolated-danger buoy',
  BOYINB: 'Installation buoy',
  BCNLAT: 'Lateral beacon',
  BCNCAR: 'Cardinal beacon',
  BCNSAW: 'Safe-water beacon',
  BCNSPP: 'Special beacon',
  BCNISD: 'Isolated-danger beacon',
  WRECKS: 'Wreck',
  OBSTRN: 'Obstruction',
  UWTROC: 'Underwater rock',
  DEPARE: 'Depth area',
  DRGARE: 'Dredged area',
  DEPCNT: 'Depth contour',
  SOUNDG: 'Sounding',
  COALNE: 'Coastline',
  LNDARE: 'Land',
  SEAARE: 'Sea area',
  BUAARE: 'Built-up area',
  BUISGL: 'Building',
  PONTON: 'Pontoon',
  SLCONS: 'Shoreline construction',
  HRBFAC: 'Harbour facility',
  MORFAC: 'Mooring',
  ACHARE: 'Anchorage',
  FAIRWY: 'Fairway',
  RECTRC: 'Recommended track',
  NAVLNE: 'Navigation line',
  BRIDGE: 'Bridge',
  RIVERS: 'River',
  ROADWY: 'Road',
  RAILWY: 'Railway',
  M_QUAL: 'Quality of data',
  M_COVR: 'Coverage',
};

// Normalised buoy/beacon category -> plain name.
const CATEGORY: Record<string, string> = {
  port: 'Port hand',
  stbd: 'Starboard hand',
  preferred_port: 'Preferred channel to port',
  preferred_stbd: 'Preferred channel to starboard',
  ncard: 'North cardinal',
  ecard: 'East cardinal',
  scard: 'South cardinal',
  wcard: 'West cardinal',
  safe_water: 'Safe water',
  isolated_danger: 'Isolated danger',
  special: 'Special mark',
};

// Category of landmark (CATLMK) -> plain name.
const CATLMK: Record<string, string> = {
  '1': 'Cairn',
  '3': 'Chimney',
  '5': 'Flagstaff',
  '6': 'Flare stack',
  '7': 'Mast',
  '9': 'Monument',
  '15': 'Spire',
  '17': 'Lighthouse',
  '18': 'Radio mast',
  '19': 'Tower',
  '20': 'Windmill',
};

// Category of obstruction (CATOBS) -> plain name.
const CATOBS: Record<string, string> = {
  '1': 'Snag or stump',
  '2': 'Wellhead',
  '3': 'Diffuser',
  '4': 'Crib',
  '5': 'Fish haven',
  '6': 'Foul ground',
  '7': 'Foul area',
  '8': 'Ice boom',
  '9': 'Ground tackle',
  '10': 'Boom',
};

// Water level (WATLEV) -> plain name, full and short.
const WATLEV: Record<string, string> = {
  '1': 'Partly submerged at high water',
  '2': 'Always dry',
  '3': 'Always submerged',
  '4': 'Covers and uncovers',
  '5': 'Awash',
  '6': 'Subject to inundation',
  '7': 'Floating',
};
const WATLEV_SHORT: Record<string, string> = {
  '1': 'partly submerged',
  '2': 'dries',
  '3': 'submerged',
  '4': 'covers and uncovers',
  '5': 'awash',
  '6': 'floods',
  '7': 'floating',
};

// Nature of surface (NATSUR) -> plain name.
const NATSUR: Record<string, string> = {
  '1': 'Mud',
  '2': 'Clay',
  '3': 'Silt',
  '4': 'Sand',
  '5': 'Stone',
  '6': 'Gravel',
  '7': 'Pebbles',
  '8': 'Cobbles',
  '9': 'Rock',
  '11': 'Lava',
  '14': 'Coral',
  '17': 'Shells',
  '18': 'Boulder',
};

// Shape (BOYSHP/BCNSHP normalised) -> plain name.
const SHAPE: Record<string, string> = {
  conical: 'Conical',
  can: 'Can',
  spherical: 'Spherical',
  pillar: 'Pillar',
  spar: 'Spar',
  barrel: 'Barrel',
  'super-buoy': 'Super-buoy',
  'ice-buoy': 'Ice buoy',
};

function titleCase(s: string): string {
  return s
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Plain name for an S-57 object class, falling back to a tidied acronym. */
export function objClassLabel(objClass: string | undefined): string {
  if (!objClass) return 'Charted object';
  return OBJCLASS[objClass] ?? titleCase(objClass.toLowerCase());
}

/** Plain name(s) for a colour string of letters like 'W', 'R', 'W/G'. */
export function colourLabel(colour: unknown): string | null {
  if (colour == null) return null;
  const letters = String(colour)
    .split('/')
    .map((c) => c.trim())
    .filter(Boolean);
  if (!letters.length) return null;
  return letters.map((c) => COLOUR[c] ?? c).join(' / ');
}

/** Plain name for a buoy/beacon category. */
export function categoryLabel(category: unknown): string | null {
  if (category == null) return null;
  return CATEGORY[String(category)] ?? titleCase(String(category));
}

/** Plain name for a buoy/beacon shape. */
export function shapeLabel(shape: unknown): string | null {
  if (shape == null) return null;
  return SHAPE[String(shape)] ?? titleCase(String(shape));
}

/** Plain name(s) for one or more CATLMK landmark category codes. */
export function catlmkLabel(catlmk: unknown): string | null {
  if (catlmk == null) return null;
  const codes = Array.isArray(catlmk) ? catlmk : [catlmk];
  const names = codes.map((c) => CATLMK[String(c)]).filter(Boolean) as string[];
  return names.length ? names.join(', ') : null;
}

/** True when a landmark's CATLMK marks it as a lighthouse (code 17). */
export function isLighthouse(catlmk: unknown): boolean {
  const codes = Array.isArray(catlmk) ? catlmk : [catlmk];
  return codes.some((c) => String(c) === '17');
}

/** Normalise a coded value (e.g. 6, 6.0, '6', ['6']) to its integer-string key. */
function codeKey(v: unknown): string | null {
  const first = Array.isArray(v) ? v[0] : v;
  const n = typeof first === 'number' ? first : parseFloat(String(first));
  return Number.isFinite(n) ? String(Math.trunc(n)) : null;
}

/** Plain name for an obstruction category (CATOBS). */
export function catobsLabel(v: unknown): string | null {
  const k = codeKey(v);
  return k ? (CATOBS[k] ?? null) : null;
}

/** Plain name for a water level (WATLEV), full or short form. */
export function watlevLabel(v: unknown, short = false): string | null {
  const k = codeKey(v);
  if (!k) return null;
  return (short ? WATLEV_SHORT[k] : WATLEV[k]) ?? null;
}

/** Plain name(s) for nature of surface (NATSUR), which may be a list. */
export function natsurLabel(v: unknown): string | null {
  if (v == null) return null;
  const codes = Array.isArray(v) ? v : [v];
  const names = codes.map((c) => NATSUR[codeKey(c) ?? '']).filter(Boolean) as string[];
  return names.length ? names.join(', ') : null;
}

function asNum(v: unknown): number | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

/** Parse an S-57 SIGGRP like "(2)" or "(2+1)" into "2" / "2+1", or null. */
function signalGroup(siggrp: unknown): string | null {
  if (siggrp == null) return null;
  const g = String(siggrp).replace(/[()]/g, '').trim();
  if (!g || g === '1') return null;
  return g;
}

export interface LightReading {
  /** Standard chart abbreviation, e.g. "Fl(2).R.6s10M". */
  abbrev: string;
  /** Plain English, e.g. "Flashing, 2 every 6 seconds, red, range 10 nm". */
  plain: string;
}

/**
 * Read a light's character into the standard abbreviation plus a plain-English
 * sentence, from the raw S-57 attrs (LITCHR, SIGGRP, SIGPER, VALNMR) and the
 * normalised colour letters. `abbrev` falls back to the converter's
 * `lightCharacter` string when one is supplied.
 */
export function lightReading(
  raw: Record<string, unknown>,
  colourLetters: unknown,
  abbrevFallback?: string,
): LightReading {
  const code = asNum(raw.LITCHR);
  const litchr = code != null ? LITCHR[code] : undefined;
  const group = signalGroup(raw.SIGGRP);
  const period = asNum(raw.SIGPER);
  const range = asNum(raw.VALNMR);
  const colour = colourLabel(colourLetters);

  // Abbreviation (prefer the converter's composed string, else build one).
  let abbrev = abbrevFallback ?? '';
  if (!abbrev && litchr) {
    abbrev = litchr[0];
    if (group) abbrev += `(${group})`;
    if (colourLetters && String(colourLetters) !== 'W') abbrev += `.${String(colourLetters)}`;
    if (period != null) abbrev += `.${period}s`;
    if (range != null) abbrev += `${range}M`;
  }

  // Plain English.
  const parts: string[] = [];
  if (litchr) parts.push(litchr[1]);
  if (group && period != null) parts.push(`${group} every ${period} seconds`);
  else if (group) parts.push(`${group} flashes`);
  else if (period != null) parts.push(`period ${period} seconds`);
  if (colour) parts.push(colour);
  if (range != null) parts.push(`range ${range} nautical miles`);

  return { abbrev, plain: parts.join(', ') };
}

export interface SectorReading {
  fromDeg: number;
  toDeg: number;
  colour: string | null;
  rangeNm: number | null;
}

/** A sector's bearing range, colour and range, from one LIGHTS feature's raw. */
export function sectorReading(
  raw: Record<string, unknown>,
  colourLetters: unknown,
): SectorReading | null {
  const from = asNum(raw.SECTR1);
  const to = asNum(raw.SECTR2);
  if (from == null || to == null) return null;
  return { fromDeg: from, toDeg: to, colour: colourLabel(colourLetters), rangeNm: asNum(raw.VALNMR) };
}


// --- Zones of Confidence (CATZOC / ZOC) --------------------------------------

export interface ZocBand {
  /** The ZOC letter as charted, e.g. 'A1', 'B', 'U'. */
  band: string;
  /** Swatch colour, matched to the data-quality overlay fill. */
  colour: string;
  /** Plain-English horizontal position accuracy, e.g. '±50 m'. */
  accuracy: string;
  /** A short plain-English meaning of the band. */
  meaning: string;
}

/**
 * The six ZOC bands, with the standard horizontal position accuracy each one
 * implies and a short meaning. The colours match the data-quality overlay fill
 * in `src/map/style/layers.ts`, so a legend swatch reads the same as the chart.
 */
export const ZOC_BANDS: ZocBand[] = [
  { band: 'A1', colour: '#2ecc71', accuracy: '±5 m', meaning: 'Fully surveyed to a modern standard, seafloor searched' },
  { band: 'A2', colour: '#f1c40f', accuracy: '±20 m', meaning: 'Surveyed to a good standard' },
  { band: 'B', colour: '#e67e22', accuracy: '±50 m', meaning: 'Surveyed, but uncharted hazards may still exist' },
  { band: 'C', colour: '#e74c3c', accuracy: '±500 m', meaning: 'Low-accuracy or old survey, anomalies likely' },
  { band: 'D', colour: '#9b59b6', accuracy: 'worse than ±500 m', meaning: 'Poor-quality or very old survey' },
  { band: 'U', colour: '#7f8c8d', accuracy: 'not assessed', meaning: 'Survey quality has not been assessed' },
];

// Raw S-57 CATZOC code on M_QUAL polygons -> ZOC band letter.
const ZOC_BY_CODE: Record<number, string> = { 1: 'A1', 2: 'A2', 3: 'B', 4: 'C', 5: 'D', 6: 'U' };

/** ZOC band letter (e.g. 'B') from a raw CATZOC code (e.g. 3, '3', [3]). */
export function zocBandFromCatzoc(v: unknown): string | null {
  const k = codeKey(v);
  return k ? (ZOC_BY_CODE[Number(k)] ?? null) : null;
}

/** Full ZocBand record for a band letter like 'B', or null if unknown. */
export function zocBand(zoc: unknown): ZocBand | null {
  if (zoc == null) return null;
  const key = String(zoc).toUpperCase();
  return ZOC_BANDS.find((b) => b.band === key) ?? null;
}

/**
 * A plain-English position-accuracy sentence for a ZOC band, leading with the
 * accuracy a person actually understands and keeping the ZOC letter in brackets
 * for the chart-literate. Returns null for an unknown band.
 */
export function zocAccuracyText(zoc: unknown): string | null {
  const b = zocBand(zoc);
  if (!b) return null;
  if (b.band === 'U') return 'Chart accuracy here has not been assessed (ZOC U)';
  if (b.band === 'D') return 'Charted positions here may be off by more than ±500 m (ZOC D)';
  return `Charted positions here accurate to about ${b.accuracy} (ZOC ${b.band})`;
}


// --- Bridge / overhead clearance ---------------------------------------------

/** Charted vertical clearance (metres) from a BRIDGE's VERCLR, or null. */
export function verticalClearanceM(raw: Record<string, unknown>): number | null {
  return asNum(raw.VERCLR);
}

/**
 * Clearance now: the charted vertical clearance minus the current height of
 * tide. Returns null when there's no tidal height to subtract, which is the
 * case today (the cells carry no tide-height data), so the caller simply omits
 * the "now" figure until a tide-height feed exists. Pure so it's easy to test
 * and the live figure becomes a one-line wire-up later.
 */
export function clearanceNowM(chartedM: number | null, tidalHeightM: number | null): number | null {
  if (chartedM == null || tidalHeightM == null) return null;
  return chartedM - tidalHeightM;
}
